import React, { useContext, useEffect, useState } from "react";
import Panel from "./Panel";
import {
  Button,
  Dimmer,
  Divider,
  Grid,
  GridColumn,
  Loader,
  Message,
  Segment,
  Label,
} from "semantic-ui-react";
import { Link, useParams, useLocation } from "react-router-dom";
import { authContext } from "../containers/AuthProvider";
import { searchServices } from "../libs/requests/serviceSearch";
import { getVodafoneService } from "../libs/requests/vodafoneServiceSearch";
import Faults from "./Diagnostics/Faults";
import { getDiagnostics } from "../libs/requests/getDiagnostics";

const zenFields = [
  { key: "serviceId", label: "ServiceId" },
  { key: "phoneNumber", label: "CLI" },
  { key: "customerReference", label: "Customer Ref" },
  { key: "serviceStatus", label: "Service Status" },
  { key: "startDate", label: "Service Start Date" },
  {
    key: "contractEndDate",
    label: "Contract End Date",
    format: (dateStr) => {
      if (!dateStr) return "N/A";
      return new Date(dateStr).toLocaleDateString("en-GB");
    }
  },
  { divider: true },
  { key: "productDescription", label: "Product" },
  { key: "careLevel", label: "Care Level" },
  { key: "trafficWeighting", label: "Traffic Weighting" },
  { key: "network", label: "Network" },
  { key: "upstreamSpeed", label: "Upstream Speed" },
];

const vodafoneFields = [
  { key: "cli",               label: "CLI",               hideIfEmpty: true },
  { key: "customerReference", label: "Customer Ref",      hideIfEmpty: true },
  { key: "serviceStatus",     label: "Service Status" },
  { key: "startDate",         label: "Service Live Date", hideIfEmpty: true },
  { divider: true },
  { key: "product",           label: "Product",           hideIfEmpty: true },
  { key: "productCode",       label: "Product Code",      hideIfEmpty: true },
  { key: "careLevel",         label: "Care Level" },
  { key: "accessLineId",      label: "Access Line ID",    hideIfEmpty: true },
  { key: "ontReference",      label: "ONT Reference",     hideIfEmpty: true },
  { key: "contactName",       label: "End User Contact",  hideIfEmpty: true },
  { key: "contactPhone",      label: "Contact Number",    hideIfEmpty: true },
];

const addressFields = [
  { key: "organisationName", label: "Organisation" },
  { key: "subPremiseName", label: "Sub Premise" },
  { key: "premiseName", label: "Premise" },
  { key: "thoroughfareNumber", label: "House Number" },
  { key: "thoroughfare", label: "Street" },
  { key: "dependentThoroughfare", label: "Dependent Thoroughfare" },
  { key: "postTown", label: "Town" },
  { key: "postCode", label: "Post Code" },
];

const GridItem = ({ field, value }) => {
  const { divider, label, hideIfEmpty } = field;
  if (hideIfEmpty && !value) return null;
  const displayValue = value && field.format ? field.format(value) : value;

  if (divider)
    return <GridColumn width={16}><Divider /></GridColumn>;

  return (
    <GridColumn key={label}>
      <Grid columns={2}>
        <GridColumn><strong>{label}</strong></GridColumn>
        <GridColumn>{displayValue}</GridColumn>
      </Grid>
    </GridColumn>
  );
};

export default function ViewService() {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [service, setService] = useState({});
  const [isVodafone, setIsVodafone] = useState(false);

  const { reference } = useParams();
  const { filterByPartner } = useParams();
  const location = useLocation();

  const { permissions } = useContext(authContext);
  const [serviceStatus, setServiceStatus] = useState("");
  const [connectionDetails, setConnectionDetails] = useState({});
  const [exchangeStatus, setExchangeStatus] = useState("");
  const [broadbandConnection, setBroadbandConnection] = useState({});
  const [requestPairs] = useState(["ConnectionStatus", "MajorServiceOutage", "BroadbandConnection"]);

  useEffect(() => {
    (async () => {
      try {
        if (!permissions.servicesAccess) return;
        setIsLoading(true);

        // Detect Vodafone services by query param (set by VodafoneServicesTable link)
        // or by reference prefix (VFP = FTTP, VFS = SOGEA).
        const params = new URLSearchParams(location.search);
        const supplierParam = params.get("supplier");
        const vodafone =
          supplierParam === "Vodafone" ||
          reference?.toUpperCase().startsWith("VFP") ||
          reference?.toUpperCase().startsWith("VFS");

        setIsVodafone(vodafone);

        let newService;
        if (vodafone) {
          newService = await getVodafoneService(reference);
        } else {
          const singleService = await searchServices(reference, 0, 1, filterByPartner);
          newService = singleService?.services[0];
        }

        setService(newService ?? {});
        setServiceStatus(
          newService?.serviceStatus === "CeasedWithSupplier" ||
          newService?.serviceStatus === "ActiveCeasePending"
            ? newService.serviceStatus
            : "Active"
        );
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setIsError(true);
        setIsLoading(false);
      }
    })();
  }, []);

  // Only fetch ZEN diagnostics for ZEN services
  useEffect(() => {
    if (isVodafone) return;
    (async () => {
      try {
        if (permissions.servicesAccess) {
          const response = await getDiagnostics(reference, requestPairs);
          setConnectionDetails(response?.zenConnectionResponse || null);
          setExchangeStatus(
            response?.majorServiceOutage?.Results?.length > 0
              ? "Outage detected"
              : "No known outages"
          );
          setBroadbandConnection(response?.broadbandConnection || {});
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, [isVodafone]);

  if (!isLoading && !permissions.servicesAccess) {
    return <Panel><Message error header="You don't have permission to view this page" /></Panel>;
  }

  const fields = isVodafone ? vodafoneFields : zenFields;

  const header = (
    <>
      <span>{`Service Reference: ${reference}`}</span>
      <strong style={{ float: "right" }}>{service ? service.status : ""}</strong>
    </>
  );

  return (
    <>
      <Segment>
        <Dimmer active={isLoading} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>

        <Panel header={"Service Details"}>
          {!isVodafone &&
            service.serviceStatus === "Active" &&
            (service?.productCode?.toUpperCase()?.startsWith("FTTP") ||
              service?.productCode?.toUpperCase()?.startsWith("SOGEA") ||
              service?.productCode?.toUpperCase()?.startsWith("GFAST") ||
              service?.productCode?.toUpperCase()?.startsWith("FTTC") ||
              service?.productCode?.toUpperCase()?.startsWith("ADSL")) && (
              <Link to={`/Diagnostics/${reference}`}>
                <Button primary className="button-primary" content="Diagnostics"
                  icon="wrench" labelPosition="left" floated="right" />
              </Link>
            )}

          <Grid columns={4} style={{ padding: "20px 0" }}>
            <span>
              <Label style={{
                color: "black",
                backgroundColor:
                  serviceStatus === "CeasedWithSupplier" || serviceStatus === "ActiveCeasePending"
                    ? "grey" : "green",
                fontWeight: "bold"
              }}>
                Service Status: {serviceStatus}
              </Label>
            </span>

            {/* Supplier badge */}
            <span style={{ marginLeft: "8px" }}>
              <Label style={{
                color: "white",
                backgroundColor: isVodafone ? "#e60000" : "#009fe3",
                fontWeight: "bold"
              }}>
                {isVodafone ? "Vodafone" : "ZEN"}
              </Label>
            </span>

            {!isVodafone && connectionDetails && !permissions.isL2 && serviceStatus === "Active" && (
              <Link to={{ pathname: `/Diagnostics/Connection/${reference}` }}>
                <Label style={{
                  color: "black",
                  backgroundColor: connectionDetails.model?.status === "Connected" ? "green" : "red",
                  fontWeight: "bold"
                }}>
                  Connection Status: {connectionDetails.model?.status}
                </Label>
                <br />
                <strong>View details</strong>
              </Link>
            )}

            {!isVodafone && (
              <span>
                <Label style={{
                  color: "black",
                  backgroundColor: exchangeStatus === "Outage detected" ? "red" : "green",
                  fontWeight: "bold"
                }}>
                  Exchange status: {exchangeStatus}
                </Label>
              </span>
            )}
          </Grid>
        </Panel>

        <Panel header={header}>
          {isError && (
            <Message error header="There was an error trying to view this service, please try again later" />
          )}
          {!service && !isError && (
            <Message info header="The service could not be found" />
          )}
          {!!service && (
            <Grid columns={3}>
              {fields.map((field) => (
                <GridItem field={field} value={service[field.key]} key={field.key ?? field.label} />
              ))}
            </Grid>
          )}
        </Panel>

        <Panel header={"Installation Address"}>
          <Grid columns={3}>
            {addressFields.map((field) => (
              <GridItem field={field} value={service[field.key]} key={field.key} />
            ))}
          </Grid>
        </Panel>

        {!isVodafone && !permissions.isL2 && (
          <Panel header={"Connection Details"}>
            <Grid columns={3}>
              <Label>Username: {broadbandConnection?.username ?? ""}</Label>
              <Label>Password: {broadbandConnection?.password ?? ""}</Label>
              <Label>Router IP Address: {broadbandConnection?.routerIpAddress ?? ""}</Label>
            </Grid>
          </Panel>
        )}

        {isVodafone && !permissions.isL2 && (service?.pppoeUsername || service?.pppoePassword) && (
          <Panel header={"Connection Details"}>
            <Grid columns={3}>
              {service.pppoeUsername && <Label>PPPoE Username: {service.pppoeUsername}</Label>}
              {service.pppoePassword && <Label>PPPoE Password: {service.pppoePassword}</Label>}
            </Grid>
          </Panel>
        )}

        {!isVodafone && <Faults reference={reference} />}
      </Segment>

      <Link to="/Services">
        <Button primary className="button-primary">Back</Button>
      </Link>
    </>
  );
}
